import { getHostApis } from './getHost'
import { getAvanquestSearchUrl } from './avanquest/getAvanquestSearchUrl'
import { isAvanquest } from '../tenants/isAvanquest'
import debug from '../../utils/console'

const logger = debug.withTag('utils:getSearchUrl')

interface GetSearchUrlOption {
  fallbackUrl: string
  queryPlaceholder: string
}

export interface GetSearchUrl {
  url: string
  isMonetized?: boolean
}

export async function getSearchUrl(
  searchTerm: string,
  options: Partial<GetSearchUrlOption>
): Promise<GetSearchUrl> {
  const { fallbackUrl = '', queryPlaceholder = '{searchTerms}' } = {
    ...options,
  }

  try {
    // Check for Avanquest variant first (wc.onenews.com, staging, and localhost for dev)
    // Note: This intentionally bypasses browser API checks (shift/onelaunch) below
    // because Avanquest variants require specific lusearch.com URLs with fixed PIDs
    if (import.meta.client && typeof window !== 'undefined' && isAvanquest()) {
      // Try to get the variant from the URL query params
      const urlParams = new URLSearchParams(window.location.search)
      const variant = urlParams.get('variant')

      if (variant === 'branded') {
        return getAvanquestSearchUrl(searchTerm, 'NT260101')
      }
      if (variant === 'unbranded') {
        return getAvanquestSearchUrl(searchTerm, 'NT260102')
      }
      // Default variant (no ?variant param) falls through to normal behavior
    }

    const generateSearchUrl = (searchUrl: string) =>
      searchUrl.replace(queryPlaceholder, encodeURIComponent(searchTerm))

    const { shift, onelaunch } = getHostApis()

    const newShiftApi = shift?.search?.getDefault
    if (newShiftApi) {
      const searchPreferences = await newShiftApi()
      const isMonetized = searchPreferences.monetized
      const url = generateSearchUrl(searchPreferences.url)
      return { url, isMonetized }
    }

    // TODO: remove after the new api is the standard for most users
    const shiftLegacyApi = shift?.getServerConfig
    if (shiftLegacyApi) {
      const updateServiceSettings = await shiftLegacyApi()
      return { url: generateSearchUrl(updateServiceSettings.search_url) }
    }

    const onelaunchApi = onelaunch?.getMergedData
    if (onelaunchApi) {
      const updateServiceSettings = await onelaunchApi()
      return { url: generateSearchUrl(updateServiceSettings.search_url) }
    }

    return { url: generateSearchUrl(fallbackUrl) }
  } catch (e) {
    logger.error(e)
    return { url: fallbackUrl }
  }
}
